<template>
  <v-dialog v-model="isModalEditarPartidaVisible" max-width="400">
    <v-card>
      <v-card-title> Editar partida </v-card-title>
      <v-card-text>
        <v-form ref="form" v-model="isFormValid">
          <AvisoSistemaAleman
            :resultado1="partidaEditada.resultadoUsuario1"
            :resultado2="partidaEditada.resultadoUsuario2"
          />
          <v-text-field
            :label="`Puntos de ${lado1}`"
            v-model.number="partidaEditada.resultadoUsuario1"
            type="number"
            :rules="[rules.required, rules.isNumber, rules.noNegativo]"
          ></v-text-field>

          <v-text-field
            :label="`Puntos de ${lado2}`"
            v-model.number="partidaEditada.resultadoUsuario2"
            type="number"
            :rules="[rules.required, rules.isNumber, rules.noNegativo]"
          ></v-text-field>

          <v-combobox
            v-model="partidaEditada.escenarioPartida"
            :items="listaEscenarios"
            @click="loadEscenarios"
            label="Escenario"
          ></v-combobox>

          <v-radio-group
            v-model="partidaEditada.liderMuertoUsuario1"
            :label="`¿${lado1} ${companero1 ? 'han' : 'ha'} matado al general?`"
            :rules="[rules.isBool]"
          >
            <v-radio label="Sí" :value="true"></v-radio>
            <v-radio label="No" :value="false"></v-radio>
          </v-radio-group>

          <!-- Radio group para el líder de usuario 2 -->
          <v-radio-group
            v-model="partidaEditada.liderMuertoUsuario2"
            :label="`¿${lado2} ${companero2 ? 'han' : 'ha'} matado al general?`"
            :rules="[rules.isBool]"
          >
            <v-radio label="Sí" :value="true"></v-radio>
            <v-radio label="No" :value="false"></v-radio>
          </v-radio-group>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-btn
          small
          variant="tonal"
          color="primary"
          @click="confirmarEditarPartida"
          :disabled="!isFormValid"
        >
          Confirmar
        </v-btn>
        <v-btn small variant="tonal" color="secondary" @click="cerrarModal">
          Cancelar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { UpdatePartidaTorneoDTO } from "@/interfaces/Live";
import { computed, ref, watch } from "vue";
import AvisoSistemaAleman from "@/components/GestionTorneos/AvisoSistemaAleman.vue";
import { PartidaTorneoDTO, PartidaTorneoMasDTO } from "@/interfaces/Partidas";
import { appsettings } from "@/settings/appsettings";

//eslint-disable-next-line no-undef
const props = defineProps<{
  isVisible: boolean;
  partida: PartidaTorneoDTO | PartidaTorneoMasDTO;
  idUsuario: number;
}>();

//eslint-disable-next-line no-undef
const emit = defineEmits(["confirmar", "cerrar"]);

// En Parejas cada lado son el capitán y su compañero
const companero1 = computed(
  () => (props.partida as PartidaTorneoDTO).nickCompanero1 ?? null
);
const companero2 = computed(
  () => (props.partida as PartidaTorneoDTO).nickCompanero2 ?? null
);
const lado1 = computed(() =>
  companero1.value
    ? `${props.partida.nick1} y ${companero1.value}`
    : props.partida.nick1
);
const lado2 = computed(() =>
  companero2.value
    ? `${props.partida.nick2} y ${companero2.value}`
    : props.partida.nick2
);

// Variables reactivas
const isModalEditarPartidaVisible = ref<boolean>(props.isVisible);
const partidaEditada = ref<UpdatePartidaTorneoDTO>({
  idPartidaTorneo: props.partida.idPartidaTorneo,
  resultadoUsuario1: props.partida.resultadoUsuario1,
  resultadoUsuario2: props.partida.resultadoUsuario2,
  escenarioPartida: props.partida.escenarioPartida,
  ganadorPartidaTorneo: props.partida.ganadorPartidaTorneo,
  partidaValidadaUsuario1: props.partida.partidaValidadaUsuario1,
  partidaValidadaUsuario2: props.partida.partidaValidadaUsuario2,
  liderMuertoUsuario1: props.partida.liderMuertoUsuario1,
  liderMuertoUsuario2: props.partida.liderMuertoUsuario2,
});
const isFormValid = ref<boolean>(false);

const listaEscenarios = ref<string[]>([]);
const isLoading = ref<boolean>(false);
const isLoadingEscenarios = ref<boolean>(false);

// Cerrar el modal
const cerrarModal = () => {
  emit("cerrar");
};

// Confirmar los puntos y cerrar el modal
const confirmarEditarPartida = () => {
  if (props.idUsuario == 1) {
    partidaEditada.value.partidaValidadaUsuario1 = true;
  } else {
    partidaEditada.value.partidaValidadaUsuario2 = true;
  }

  emit("confirmar", partidaEditada.value);
  cerrarModal();
};

// Monitorea cambios en la visibilidad del modal y en la prop 'partida'
watch(
  () => props.isVisible,
  newVal => {
    isModalEditarPartidaVisible.value = newVal;
  }
);

watch(
  () => props.partida,
  newPartida => {
    partidaEditada.value = { ...newPartida };
  },
  { immediate: true }
);

const loadEscenarios = async () => {
  isLoading.value = true;
  isLoadingEscenarios.value = true;

  listaEscenarios.value = await appsettings.escenarios;

  isLoading.value = false;
  isLoadingEscenarios.value = false;
};

// Reglas de validación
const rules = {
  // El 0 es un resultado válido: con !!value había que borrarlo y volver a escribirlo
  required: (value: unknown) =>
    (value !== null && value !== undefined && value !== "") ||
    "Este campo es obligatorio",
  isNumber: (value: number) => !isNaN(value) || "Debe ser un número",
  // La base de datos no admite resultados negativos
  noNegativo: (value: number) => Number(value) >= 0 || "No puede ser negativo",
  isBool: (value: boolean) =>
    value == true || value == false || "Este campo es obligatorio",
};
</script>
